import React, { useState } from "react"
import { Flex, IconButton, Text } from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import { MenuItem } from "components/dialogs/Menu"
import { EventGroup, GuestFilterType, ModalName } from "sharedTypes"
import useModal from "services/useModal"
import { asNumber } from "utilities/strings"
import FilterIcon, { TABULAR_NUMS_SX } from "../FilterIcon"

type Props = {
  type: GuestFilterType;
  label: string;
  groupId: boolean;
  segmentId: string;
  icon?: string;
  iconColor?: string;
  onSelect: () => {};
  selected?: boolean;
  count: number;
  eventGroup?: EventGroup;
  onCloseMenu: () => void;
}

const ActionButton = ({
  label, iconName, iconSize, mr, onClick, onHover,
}: {
  label: string; iconName: string; iconSize: number; mr?: number;
  onClick: (e: React.MouseEvent) => void; onHover: (hovered: boolean) => void;
}) => (
  <IconButton
    aria-label={label}
    title={label}
    icon={<Icon size={iconSize} icon={iconName} color="blue.500" />}
    variant="ghost"
    size="xs"
    borderRadius="md"
    mr={mr}
    _hover={{ bg: "gray.200" }}
    onClick={onClick}
    onMouseEnter={() => onHover(true)}
    onMouseLeave={() => onHover(false)}
  />
)

const EditableFilter = ({
  type, label, groupId, segmentId,
  icon, iconColor, onSelect, selected, count, eventGroup, onCloseMenu,
}: Props) => {
  const showModal = useModal()
  const [actionHovered, setActionHovered] = useState(false)

  const handleEdit = () => {
    if (type === GuestFilterType.CustomSegment) {
      showModal(ModalName.SegmentForm, { segmentId })
    } else {
      showModal(ModalName.GuestListForm, { eventGroupId: groupId })
    }
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCloseMenu()
    handleEdit()
  }

  // Bell-icon action lands directly on the Check-In Alert sub-screen
  // instead of the Main one — the user already declared their intent.
  const handleAlertsClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCloseMenu()
    showModal(ModalName.GuestListForm, {
      eventGroupId: groupId, openOnCheckInAlert: true,
    })
  }

  return (
    <MenuItem
      as="div"
      onClick={onSelect}
      fontSize="md"
      bg={selected ? "blue.50" : undefined}
      px={4}
      py={2.5}
      sx={{
        cursor: "pointer",
        ...(actionHovered && {
          "&:hover, &:active, &[data-active]": {
            bg: selected ? "blue.50" : "transparent",
          },
        }),
      }}
    >
      <Flex align="center" justify="space-between" w="full">
        <Flex align="center" flex={1} overflow="hidden" mr={4}>
          <FilterIcon
            eventGroup={eventGroup}
            icon={icon}
            iconColor={iconColor}
            size={4}
          />
          <Text isTruncated>{label}</Text>
        </Flex>
        {
          !!eventGroup?.notifications?.length
          && <ActionButton label="View alerts" iconName="checkin-alert" iconSize={5} mr={1} onClick={handleAlertsClick} onHover={setActionHovered} />
        }
        <Text as="span" flexShrink={0} mr={2} sx={TABULAR_NUMS_SX}>
          {asNumber(count)}
        </Text>
        <ActionButton label="Edit" iconName="website-edit" iconSize={4} onClick={handleEditClick} onHover={setActionHovered} />
      </Flex>
    </MenuItem>
  )
}

export default EditableFilter
